import {
  Link,
  Box,
  Image,
  IconButton,
  VStack,
  HStack,
  Flex,
  DialogRoot,
  DialogBackdrop,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogCloseTrigger
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { ListIcon, XIcon } from '@phosphor-icons/react';
import logoImage from '@/travo/assets/images/shared/logo-with-text.svg';
import DownloadButtons from '@/travo/components/ui/buttons/DownloadButtons';
import { useCopy } from '@/travo/i18n';
import { INK_RULE } from '@/travo/theme/constants';
import { LanguageMenu } from './LanguageMenu';
import { useNavItems } from './useNavItems';

interface MobileNavProps {
  isOpen: boolean;
  onToggle: (open: boolean) => void;
}

interface MenuBodyProps {
  /** Called after any choice so the dialog closes on navigation. */
  onNavigate: () => void;
}

const MenuHeader = () => {
  const copy = useCopy();

  return (
    <DialogHeader borderBottom={INK_RULE.dashed} p="20px 24px">
      <Flex justify="space-between" align="center" w="100%">
        <Image src={logoImage} h="34px" alt="Travo" />
        <DialogCloseTrigger asChild>
          <IconButton variant="ghost" aria-label={copy.nav.closeMenu} color="ink" position="static">
            <XIcon size={24} weight="bold" />
          </IconButton>
        </DialogCloseTrigger>
      </Flex>
    </DialogHeader>
  );
};

/** Page links, language switch and store badges. */
const MenuBody = ({ onNavigate }: MenuBodyProps) => {
  const copy = useCopy();
  const { links } = useNavItems();

  return (
    <DialogBody p="24px">
      <VStack as="nav" aria-label={copy.nav.primary} gap={5} align="stretch">
        {links.map((link) => (
          <Link key={link.to} asChild color="ink" fontWeight="700" fontSize="lg" py="4px">
            <RouterLink to={link.to} onClick={onNavigate}>
              {link.label}
            </RouterLink>
          </Link>
        ))}
        <HStack>
          <LanguageMenu onNavigate={onNavigate} />
        </HStack>
        <Box mt={2} onClick={onNavigate}>
          <DownloadButtons size={48} />
        </Box>
      </VStack>
    </DialogBody>
  );
};

export const MobileNav = ({ isOpen, onToggle }: MobileNavProps) => {
  const copy = useCopy();

  return (
    <>
      <IconButton display={{ base: 'flex', md: 'none' }} onClick={() => onToggle(true)} aria-label={copy.nav.openMenu} variant="ghost" color="ink">
        <ListIcon size={28} weight="bold" />
      </IconButton>

      <DialogRoot open={isOpen} onOpenChange={(e) => onToggle(e.open)} size="sm" placement="center" motionPreset="slide-in-bottom" closeOnInteractOutside>
        <DialogBackdrop bg="blackAlpha.600" backdropFilter="blur(4px)" position="fixed" inset="0" zIndex="modal" />
        <DialogContent
          bg="paper"
          border="2px solid"
          borderColor="ink"
          borderRadius="24px"
          maxW={{ base: '90%', sm: '400px' }}
          p="0"
          position="fixed"
          top="50vh"
          left="50%"
          transform="translate(-50%, -50%)"
          maxH="85vh"
          overflowY="auto"
          zIndex="modal"
        >
          <MenuHeader />
          <MenuBody onNavigate={() => onToggle(false)} />
        </DialogContent>
      </DialogRoot>
    </>
  );
};
